import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

export interface ResizeObserverEntry {
    target: Element;
    contentRect: DOMRectReadOnly;
    borderBoxSize?: ReadonlyArray<ResizeObserverSize>;
    contentBoxSize?: ReadonlyArray<ResizeObserverSize>;
    devicePixelContentBoxSize?: ReadonlyArray<ResizeObserverSize>;
}

type ResizeCallback = (entry: ResizeObserverEntry) => void;

// Singleton manager that shares one ResizeObserver across all hooks
const observerManager = {
    observer: null as ResizeObserver | null,
    callbacks: new Map<Element, Set<ResizeCallback>>(),

    observe(element: Element, callback: ResizeCallback) {
        // Lazy initialization - create observer on first use
        if (!this.observer) {
            this.observer = new ResizeObserver(entries => {
                entries.forEach(entry => {
                    const callbacks = this.callbacks.get(entry.target);
                    callbacks?.forEach(cb => cb(entry as ResizeObserverEntry));
                });
            });
        }

        // Start observing element if not already observed
        if (!this.callbacks.has(element)) {
            this.callbacks.set(element, new Set());
            this.observer.observe(element);
        }
        this.callbacks.get(element)!.add(callback);
    },

    unobserve(element: Element, callback: ResizeCallback) {
        const callbacks = this.callbacks.get(element);
        if (callbacks) {
            callbacks.delete(callback);
            // Stop observing when no callbacks remain for this element
            if (callbacks.size === 0) {
                this.callbacks.delete(element);
                this.observer?.unobserve(element);
            }
        }
    },

    cleanup() {
        this.observer?.disconnect();
        this.observer = null;
        this.callbacks.clear();
    },
};

// For dynamic element sets - manually control which elements to observe
export function useMultiResizeObserver(
    callback: (entry: ResizeObserverEntry, element: Element) => void
) {
    const callbackRef = useRef(callback);
    const observedElements = useRef<Set<Element>>(new Set());

    const stableCallback = useCallback((entry: ResizeObserverEntry) => {
        callbackRef.current(entry, entry.target);
    }, []);

    const observeElement = useCallback(
        (element: Element) => {
            if (!observedElements.current.has(element)) {
                observedElements.current.add(element);
                observerManager.observe(element, stableCallback);
            }
        },
        [stableCallback]
    );

    const unobserveElement = useCallback(
        (element: Element) => {
            if (observedElements.current.has(element)) {
                observedElements.current.delete(element);
                observerManager.unobserve(element, stableCallback);
            }
        },
        [stableCallback]
    );

    useEffect(() => {
        return () => {
            // Cleanup: remove all tracked elements
            observedElements.current.forEach(element => {
                observerManager.unobserve(element, stableCallback);
            });
            observedElements.current.clear();
        };
    }, [stableCallback]);

    return { observeElement, unobserveElement };
}

export type ResizeObserverCallback = (entry: ResizeObserverEntry) => void;

// For existing React refs - most common pattern
// IMPORTANT: Caller MUST memoize the callback function with useCallback and include all dependencies
// that the callback uses (position, loading, etc.) to ensure proper re-observation when dependencies change
export function useResizeObserverWithRef<T extends Element>(
    ref: React.RefObject<T> | null,
    callback: ResizeObserverCallback,
    onReset?: () => void
) {
    // Create a dummy ref for null case to avoid conditional hook usage
    const dummyRef = React.useRef<T>(null);
    const actualRef = ref || dummyRef;

    // Memoize reset callback to avoid unnecessary re-runs
    const resetCallback = useCallback(() => {
        onReset?.();
    }, [onReset]);

    useLayoutEffect(() => {
        if (!actualRef || !actualRef.current) {
            resetCallback();
            return;
        }

        const element = actualRef.current;
        observerManager.observe(element, callback);

        // Trigger callback immediately with current state
        const rect = element.getBoundingClientRect();
        callback({
            target: element,
            contentRect: rect,
            borderBoxSize: undefined,
            contentBoxSize: undefined,
            devicePixelContentBoxSize: undefined,
        });

        return () => {
            observerManager.unobserve(element, callback);
        };
    }, [actualRef, callback]);
}

// For direct element references - when element changes and you need to track different elements
// IMPORTANT: Caller MUST memoize the callback function with useCallback and include all dependencies
// that the callback uses (position, loading, etc.) to ensure proper re-observation when dependencies change
export function useResizeObserverWithElement<T extends Element>(
    element: T | null,
    callback: (entry: ResizeObserverEntry) => void,
    onReset?: () => void
) {
    // Memoize reset callback to avoid unnecessary re-runs
    const resetCallback = useCallback(() => {
        onReset?.();
    }, [onReset]);

    useLayoutEffect(() => {
        if (!element) {
            resetCallback();
            return;
        }

        observerManager.observe(element, callback);

        // Trigger callback immediately with current state
        const rect = element.getBoundingClientRect();
        callback({
            target: element,
            contentRect: rect,
            borderBoxSize: undefined,
            contentBoxSize: undefined,
            devicePixelContentBoxSize: undefined,
        });

        return () => {
            observerManager.unobserve(element, callback);
        };
    }, [element, callback]);
}
